/**
 * Which environment a build runs on, in the words a person reads. One home for
 * the line so the run record and the case file's live card say it the same
 * way: the record opens with it, and the live card carries it under its head
 * while the build runs, moving from "Setting up the environment" to the
 * environment the build settled on as the run's event changes.
 */

type JsonObject = Record<string, unknown>;

/** The slice of the work record the line reads: its activity, and the summary the server derived. */
export interface BuildEnvironmentView {
  activity: ReadonlyArray<{ event: JsonObject }>;
  environment?: unknown;
}

function isObject(value: unknown): value is JsonObject {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Which environment a build installs: the Working revision, the legacy store,
 * the repository index's inferred install (YAG-831), or nothing. `waiting` is
 * the build holding for the repository's setup run before it picks one.
 */
export type BuildEnvironmentSource = "working" | "legacy" | "index" | "none" | "waiting";

/** Why a build that waited for setup ends on the inferred install. */
export type BuildEnvironmentFallbackReason = "needs_you" | "failed" | "timed_out";

const BUILD_ENVIRONMENT_SOURCES: ReadonlySet<string> = new Set([
  "working",
  "legacy",
  "index",
  "none",
  "waiting",
]);

const FALLBACK_REASONS: ReadonlySet<string> = new Set(["needs_you", "failed", "timed_out"]);

export const BUILD_ENVIRONMENT_LINES: Record<Exclude<BuildEnvironmentSource, "waiting">, string> = {
  working: "Building with the Working revision",
  legacy: "Building with the repository's saved setup commands",
  index: "Building with the inferred install (no environment set up yet)",
  none: "Building with no environment",
};

const FALLBACK_WORDS: Record<BuildEnvironmentFallbackReason, string> = {
  needs_you: "needs you",
  failed: "failed",
  timed_out: "timed out",
};

/** One build environment record, as the run's event carries it. */
export interface BuildEnvironmentRecord {
  source: BuildEnvironmentSource;
  repoFullName: string | null;
  fallbackReason: BuildEnvironmentFallbackReason | null;
  /** On a `waiting` record: the delivery was taken away mid-wait and never built. */
  interrupted?: boolean;
}

function environmentRecordOf(value: unknown): BuildEnvironmentRecord | null {
  if (!isObject(value)) return null;
  const { source, repoFullName, fallbackReason, interrupted } = value;
  if (typeof source !== "string" || !BUILD_ENVIRONMENT_SOURCES.has(source)) return null;
  return {
    source: source as BuildEnvironmentSource,
    repoFullName: typeof repoFullName === "string" && repoFullName ? repoFullName : null,
    fallbackReason:
      typeof fallbackReason === "string" && FALLBACK_REASONS.has(fallbackReason)
        ? (fallbackReason as BuildEnvironmentFallbackReason)
        : null,
    ...(interrupted === true ? { interrupted: true } : {}),
  };
}

/**
 * The run's LATEST build environment record. The build writes `waiting`
 * before it holds for the repository's setup and its final source before the
 * sandbox boots, so a build in progress says what it is doing from its first
 * second and the line moves on when the wait ends. Falls back to the view's
 * summary for an older run whose activity does not carry it.
 */
export function buildEnvironmentRecord(view: BuildEnvironmentView): BuildEnvironmentRecord | null {
  let latest: BuildEnvironmentRecord | null = null;
  for (const { event } of view.activity) {
    const diagnostic = isObject(event.diagnostic) ? event.diagnostic : null;
    const record = diagnostic ? environmentRecordOf(diagnostic.environment) : null;
    if (record) latest = record;
  }
  return latest ?? environmentRecordOf(view.environment);
}

/** The source of the run's latest build environment record. */
export function buildEnvironmentSource(view: BuildEnvironmentView): BuildEnvironmentSource | null {
  return buildEnvironmentRecord(view)?.source ?? null;
}

/** The work record's first line for one build environment record. */
export function buildEnvironmentLine(record: BuildEnvironmentRecord): string {
  const repo = record.repoFullName ?? "this repository";
  if (record.source === "waiting") {
    return record.interrupted
      ? "Setup wait interrupted; the next delivery waits again"
      : `Setting up the environment for ${repo}`;
  }
  if (record.source === "index" && record.fallbackReason) {
    return `Setup for ${repo} ${FALLBACK_WORDS[record.fallbackReason]}; this build uses the inferred install`;
  }
  return BUILD_ENVIRONMENT_LINES[record.source];
}
